import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Slide,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';

export default function ViewCoachDialog({ isView, handleClose, edifierToEditData }) {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoaded(true);
  };

  if (!edifierToEditData) return null;

  const notManagementRole = edifierToEditData?.role !== 'ADMIN' && edifierToEditData?.role !== 'SITE-LEADER';
  const userRole = edifierToEditData?.role?.toLowerCase();
  const videoUrl = edifierToEditData?.video || '';
  const youtubePattern = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]{11})/;
  const youtubeMatch = videoUrl.match(youtubePattern);
  const youtubeEmbedUrl = youtubeMatch ? `https://www.youtube.com/embed/${youtubeMatch[1]}` : '';

  return (
    <Dialog
      open={isView}
      onClose={handleClose}
      fullScreen
      scroll="body"
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      aria-labelledby="view-coach-dialog-title"
      aria-describedby="view-coach-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'background.default',
          height: '100dvh',
          maxHeight: '100dvh',
          overflowY: 'auto',
          overflowX: 'hidden',
          margin: 0,
          maxWidth: '100%',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3, md: 4 }, position: 'relative' }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            zIndex: 1,
            bgcolor: 'action.hover',
            '&:hover': {
              bgcolor: 'action.selected',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Helmet>
          <title>Edifying Teacher | {edifierToEditData?.info?.contentSpeciality || 'Profile'}</title>
        </Helmet>

        {/* Profile Title */}
        <Typography
          variant="h3"
          sx={{ color: 'text.titles', display: 'block', mb: '20px', ml: { xs: '20px', sm: '0' }, pr: 6 }}
        >
          {edifierToEditData?.fname && edifierToEditData?.lname
            ? `${edifierToEditData.fname} ${edifierToEditData.lname}'s Profile`
            : 'User Profile'}
        </Typography>

        {/* Main Profile Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
            bgcolor: 'background.paper',
            overflow: 'hidden',
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 6px 30px rgba(0, 0, 0, 0.5)' : '0px 6px 30px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4,
              }}
            >
              {/* Profile Image Section */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: { xs: '100%', md: '280px' },
                  maxWidth: { xs: '100%', md: '280px' },
                }}
              >
                {!imageLoaded && (
                  <Box
                    sx={{
                      width: { xs: 200, sm: 250 },
                      height: { xs: 200, sm: 250 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '4px solid',
                      borderColor: 'background.paper',
                      boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 16px rgba(0, 0, 0, 0.4)' : '0px 4px 16px rgba(0, 0, 0, 0.1)',
                      bgcolor: 'background.neutral',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <Avatar
                  src={edifierToEditData?.image ? `${edifierToEditData?.image}?${Date.now()}` : '/assets/ProfileAvatar.png'}
                  alt={edifierToEditData?.fname}
                  sx={{
                    width: { xs: 200, sm: 250 },
                    height: { xs: 200, sm: 250 },
                    border: '4px solid',
                    borderColor: 'primary.main',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 16px rgba(0, 0, 0, 0.4)' : '0px 4px 16px rgba(0, 0, 0, 0.1)',
                    display: imageLoaded ? 'flex' : 'none',
                    fontSize: '4rem',
                    color: 'white',
                  }}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                >
                  {!edifierToEditData?.image && edifierToEditData?.fname?.charAt(0)?.toUpperCase()}
                </Avatar>

                {/* Role Chip */}
                <Chip
                  label={notManagementRole ? (edifierToEditData?.role === 'COACH' ? 'Edifier' : 'Teacher') : 'Admin'}
                  sx={{
                    mt: 2,
                    bgcolor: edifierToEditData?.role === 'COACH' ? 'info.lighter' : 'success.lighter',
                    color: edifierToEditData?.role === 'COACH' ? 'info.dark' : 'success.dark',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    height: 'auto',
                  }}
                />
              </Box>

              {/* Profile Details Section */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Name */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'text.titles',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                        fontSize: { xs: '1.75rem', sm: '2.25rem' },
                      }}
                    >
                      {!edifierToEditData?.fname
                        ? edifierToEditData?.email?.split('@')[0]
                        : `${edifierToEditData?.fname} ${edifierToEditData?.lname}`}
                    </Typography>
                  </Box>
                </Box>

                {/* Location */}
                {notManagementRole && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: 'text.secondary', textTransform: 'capitalize' }}
                      >
                        {edifierToEditData?.info?.city || 'NA'}, {edifierToEditData?.info?.state || 'NA'}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Content Speciality */}
                <Typography
                  variant="h5"
                  sx={{ color: 'text.primary', fontWeight: 600, mb: 3, lineHeight: 1.6 }}
                >
                  {edifierToEditData?.info?.contentSpeciality || 'N/A'}
                </Typography>

                {/* Languages Spoken */}
                {notManagementRole && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Languages Spoken
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(edifierToEditData?.info?.languagesSpoken || edifierToEditData?.languagesSpoken)?.map((language, key) => (
                        <Chip
                          key={key}
                          label={language}
                          sx={{
                            bgcolor: 'primary.lighter',
                            color: 'primary.dark',
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: 'primary.light',
                          }}
                        />
                      )) || (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Expertise */}
                {notManagementRole && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Expertise
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {edifierToEditData?.info?.expertiesAndResources?.map((item, key) => (
                        <Chip
                          key={key}
                          label={item}
                          sx={{
                            bgcolor: 'primary.lighter',
                            color: 'primary.dark',
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: 'primary.light',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Who am I */}
                {notManagementRole && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Who am I?
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {edifierToEditData?.info?.interestBio || 'N/A'}
                    </Typography>
                  </Box>
                )}

                {/* Words of Encouragement */}
                {notManagementRole && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Words of Encouragement
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        bgcolor: 'background.neutral',
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {edifierToEditData?.info?.encouragementAdvice === 'test'
                        ? "This user didn't set up any bio yet"
                        : edifierToEditData?.info?.encouragementAdvice || 'No Bio Yet'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Video Section */}
        {notManagementRole && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.titles',
                  mb: 2,
                }}
              >
                Introduction Video
              </Typography>

              {edifierToEditData?.video ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'common.black',
                    aspectRatio: '16/9',
                    maxHeight: '500px',
                  }}
                >
                  {youtubeEmbedUrl ? (
                    <iframe
                      width="100%"
                      height="100%"
                      src={youtubeEmbedUrl}
                      title="Introduction Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ display: 'block', width: '100%', height: '100%' }}
                    />
                  ) : (
                    <>
                      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                      <video
                        controls
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                        src={`${edifierToEditData?.video}?${Date.now()}`}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 6,
                    textAlign: 'center',
                    bgcolor: 'background.neutral',
                    minHeight: '300px',
                    aspectRatio: '16/9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'default',
                  }}
                >
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                    }}
                  >
                    <VideocamIcon sx={{ fontSize: 60 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                    No video uploaded yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    This user hasn't uploaded an introduction video yet
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Accolades & Demographics */}
        {notManagementRole && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              bgcolor: 'background.default',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'background.default' }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.titles',
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                }}
              >
                Accolades & Demographics
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 4,
                }}
              >
                {/* Left Column */}
                <Box>
                  {edifierToEditData?.info?.nbctCertified && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Nationally Board Certified?
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                      >
                        {edifierToEditData?.info?.nbctCertified}
                      </Typography>
                    </Box>
                  )}
                  {edifierToEditData?.info?.specialAccolades && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Special Accolades
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.titles', pl: 2 }}>
                        {edifierToEditData?.info?.specialAccolades}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Right Column */}
                <Box>
                  {edifierToEditData?.info?.ethnoRacial && edifierToEditData?.info?.ethnoRacial.length > 0 && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Identity Markers
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 2 }}>
                        {edifierToEditData?.info?.ethnoRacial?.map((item, key, array) => (
                          <Typography key={key} variant="body1" sx={{ color: 'text.titles' }}>
                            {item}{key < array.length - 1 ? ',' : ''}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {edifierToEditData?.gender && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Gender
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                      >
                        {edifierToEditData?.gender}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                      Current Status
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                    >
                      {edifierToEditData?.role === 'SITE-LEADER' ? 'Site Leader' : userRole === 'coach' ? 'Edifier' : userRole}
                    </Typography>
                  </Box>
                  {edifierToEditData?.info?.districtSchool && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        District Type
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                      >
                        {edifierToEditData?.info?.districtSchool}
                      </Typography>
                    </Box>
                  )}
                  {edifierToEditData?.info?.partnerSchool && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Partner Affiliation
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.titles', pl: 2 }}>
                        {edifierToEditData?.info?.partnerSchool}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Why I Mentor / Key Areas */}
        {notManagementRole && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #8E71AD 0%, #A94B9C 100%)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 2,
                  lineHeight: 1.4,
                }}
              >
                {edifierToEditData?.role?.toLowerCase() === 'coach'
                  ? 'Why I Mentor?'
                  : 'What are key areas that you would like to concentrate most with your edifier?'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  lineHeight: 1.8,
                  fontWeight: 400,
                }}
              >
                {edifierToEditData?.info?.whatMotivates || 'NA'}
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
}
